"use client"

import { Fragment } from "react"
import { Button, Card, Col, Row } from "antd"
import ArrowLeftRightIconDownIcon from "@/components/icons/ArrowLeftRightIcon"

import SelectAirportFrom from "./SelectAirportFrom"
import SelectAirportTo from "./SelectAirportTo"

import SelectCabinClass from "./SelectCabinClass"
import ButtonSubmitSearchFlights from "./ButtonSubmitSearchFlights"
import { useAppDispatch, useAppSelector } from "@/lib/redux/hooks"
import { flightActions } from "@/lib/redux/features/flight/reduce"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faPlus, faX } from "@fortawesome/free-solid-svg-icons"
import { useTranslation } from "react-i18next"
import SelectPeopleWrapper from "./SelectPeople.wrapper"
import SelectDateFromToWrapper from "./SelectDateFromTo.wrapper"
import { FlightSearchPopoverBoundary } from "../components/SelectPlacement/FlightSearchPopoverBoundary"

type SearchingFlightsProps = {
  layout?: "default" | "hero"
}

const SearchingFlights = ({ layout = "default" }: SearchingFlightsProps) => {
  const { t } = useTranslation()
  const inputFlights = useAppSelector((state) => state.flight.inputFlights)
  const dispatch = useAppDispatch()
  const isMultiCities = useAppSelector((state) => state.flight.isMultiCities)
  const isMax = inputFlights.length >= 3
  const isHero = layout === "hero"

  return (
    <Fragment>
      {inputFlights.map((input, index) => {
        if (input.isReturn) {
          return <Fragment key={index} />
        }

        if (isHero && !isMultiCities) {
          return (
            <div key={index} className="home__searching--hero-form">
              <Row className="home__searching--hero-row" gutter={[14, 12]} align="bottom" wrap={false}>
                <Col flex="2.1 1 0" className="home__searching--hero-airports-col">
                  <FlightSearchPopoverBoundary>
                    <Row className="home__searching--hero-airports" gutter={14} align="bottom" wrap={false}>
                      <Col flex="1 1 0" className="home__searching--hero-field-col">
                        <SelectAirportFrom
                          onlyContent
                          size="small"
                          layout="hero"
                          indexInputFlight={index}
                        />
                      </Col>

                      <Col flex="none" className="home__searching--hero-swap-col">
                        <Button
                          shape="circle"
                          className="home__searching--swap-btn"
                          icon={<ArrowLeftRightIconDownIcon />}
                          onClick={() =>
                            dispatch(flightActions.swicthBtwAirports({ flightInputIndex: index }))
                          }
                          aria-label={t("flight.from")}
                        />
                      </Col>

                      <Col flex="1 1 0" className="home__searching--hero-field-col">
                        <SelectAirportTo
                          onlyContent
                          size="small"
                          layout="hero"
                          indexInputFlight={index}
                        />
                      </Col>
                    </Row>
                  </FlightSearchPopoverBoundary>
                </Col>

                <Col flex="1.35 1 0" className="home__searching--hero-field-col home__searching--hero-dates-col">
                  <SelectDateFromToWrapper
                    size="small"
                    layout="hero"
                    indexInputFlight={index}
                  />
                </Col>
              </Row>

              <Row
                className="home__searching--hero-row home__searching--hero-row--secondary home__searching--hero-form-row-2"
                gutter={[14, 12]}
                align="bottom"
                wrap={false}
              >
                <Col flex="2.1 1 0" className="home__searching--hero-airports-col">
                  <Row className="home__searching--hero-airports" gutter={14} align="bottom" wrap={false}>
                    <Col flex="1 1 0" className="home__searching--hero-field-col home__searching--hero-pax-col">
                      <SelectPeopleWrapper size="large" />
                    </Col>

                    <Col
                      flex="none"
                      className="home__searching--hero-swap-col home__searching--hero-swap-col--spacer"
                      aria-hidden
                    >
                      <span className="home__searching--hero-swap-spacer" />
                    </Col>

                    <Col flex="1 1 0" className="home__searching--hero-field-col home__searching--hero-cabin-col">
                      <SelectCabinClass size="large" />
                    </Col>
                  </Row>
                </Col>

                <Col flex="1.35 1 0" className="home__searching--hero-search-col">
                  <ButtonSubmitSearchFlights hero />
                </Col>
              </Row>
            </div>
          )
        }

        return (
          <Row
            key={index}
            gutter={[15, 15]}
            style={index < inputFlights.length - 1 ? { marginBottom: "1rem" } : {}}
          >
            <Col span={isMultiCities ? 16 : 12}>
              <Row>
                <Col span={24}>
                  <FlightSearchPopoverBoundary>
                    <Card
                      className="home__searching--airport-row"
                      styles={{
                        body: {
                          paddingBlock: 0,
                        },
                      }}
                    >
                      <Row wrap={false}>
                        <Col flex={1} style={{ paddingBlock: "1.2rem" }}>
                          <SelectAirportFrom onlyContent size="small" indexInputFlight={index} />
                        </Col>
                        <Col style={{ position: "relative", marginInline: "2.5rem" }}>
                          <Row
                            style={{
                              position: "absolute",
                              height: "100%",
                              transform: "translate(-50%, 50%)",
                              top: "-50%",
                              left: "-50%",
                            }}
                            align="middle"
                            justify="center"
                          >
                            <Col>
                              <Button
                                shape="circle"
                                style={{ marginInline: 5 }}
                                icon={<ArrowLeftRightIconDownIcon />}
                                onClick={() =>
                                  dispatch(
                                    flightActions.swicthBtwAirports({ flightInputIndex: index }),
                                  )
                                }
                              />
                            </Col>
                          </Row>
                        </Col>
                        <Col flex={1} style={{ paddingBlock: "1.2rem" }}>
                          <SelectAirportTo onlyContent size="small" indexInputFlight={index} />
                        </Col>
                      </Row>
                    </Card>
                  </FlightSearchPopoverBoundary>
                </Col>
              </Row>
              <Row gutter={15} wrap={false}>
                {!isMultiCities && (
                  <Col flex={1}>
                    <SelectPeopleWrapper />
                  </Col>
                )}
                {!isMultiCities && (
                  <Col flex={1}>
                    <SelectCabinClass />
                  </Col>
                )}
              </Row>
            </Col>
            <Col span={isMultiCities ? 8 : 12}>
              <Row gutter={[15, 15]}>
                <Col flex={isMultiCities ? 1 : undefined} span={isMultiCities ? undefined : 24}>
                  <SelectDateFromToWrapper size="small" indexInputFlight={index} />
                </Col>
                {isMax && (
                  <Col>
                    <Row align="middle" style={{ height: "100%" }}>
                      <Button
                        onClick={() =>
                          dispatch(flightActions.removeFlightInput({ flightInputIndex: index }))
                        }
                        type="primary"
                        ghost
                        danger
                        shape="circle"
                        size="large"
                        icon={<FontAwesomeIcon icon={faX} />}
                      />
                    </Row>
                  </Col>
                )}
              </Row>
              {!isMultiCities && (
                <Row style={{ marginTop: 15 }}>
                  <ButtonSubmitSearchFlights />
                </Row>
              )}
            </Col>
          </Row>
        )
      })}
      {isMultiCities && (
        <Row align="middle" justify="space-between" style={{ marginTop: "1rem" }}>
          <Col>
            {!isMax && (
              <Button
                size="large"
                type="primary"
                ghost
                icon={<FontAwesomeIcon icon={faPlus} />}
                onClick={() => dispatch(flightActions.addMoreFlightInput())}
              >
                {t("flight.add_flight")}
              </Button>
            )}
          </Col>
          <Col>
            <ButtonSubmitSearchFlights hero={isHero} />
          </Col>
        </Row>
      )}
    </Fragment>
  )
}

export default SearchingFlights
